import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, FileCheck2, Type, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MaterialsSection } from "./MaterialsSection";

function UploadBox({ assignmentId, kind, title, required, hint, current, onUploaded }) {
  const inputRef = useRef(null);
  const [mode, setMode] = useState("file");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/assignments/${assignmentId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title} added (${data.word_count} words).`);
      if (data.warning) toast.warning(data.warning);
      onUploaded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handlePaste = async () => {
    if (text.trim().length < 10) return toast.error("Please paste a bit more text.");
    setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${assignmentId}/paste`, { kind, text });
      toast.success(`${title} saved (${data.word_count} words).`);
      if (data.warning) toast.warning(data.warning);
      setText(""); onUploaded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={`upload-box-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-semibold">{title}</h3>
          {required ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700 dark:bg-red-950 dark:text-red-300">Required</span>
            : <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Optional</span>}
        </div>
        {current && <span className="flex items-center gap-1 text-xs font-medium text-[#059669]"><FileCheck2 className="h-4 w-4" /> Added</span>}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{hint}</p>

      <div className="mt-4 flex gap-2">
        <Button size="sm" variant={mode === "file" ? "default" : "outline"} onClick={() => setMode("file")} data-testid={`${kind}-mode-file`}
          className={mode === "file" ? "bg-[#0B5D3B] text-white hover:bg-[#08472D]" : ""}><UploadCloud className="mr-1.5 h-4 w-4" />Upload</Button>
        <Button size="sm" variant={mode === "paste" ? "default" : "outline"} onClick={() => setMode("paste")} data-testid={`${kind}-mode-paste`}
          className={mode === "paste" ? "bg-[#0B5D3B] text-white hover:bg-[#08472D]" : ""}><Type className="mr-1.5 h-4 w-4" />Paste text</Button>
      </div>

      {mode === "file" ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          className="mt-4 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-background/60 px-4 py-8 text-center">
          <UploadCloud className="h-7 w-7 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Drag &amp; drop, or</p>
          <input ref={inputRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" data-testid={`${kind}-file-input`}
            onChange={(e) => handleFile(e.target.files[0])} />
          <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${kind}-choose-file`}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Choose file
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">PDF, DOCX or TXT · max 50 MB</p>
        </div>
      ) : (
        <div className="mt-4">
          <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Paste your ${title.toLowerCase()} here…`} data-testid={`${kind}-paste-area`} />
          <Button size="sm" className="mt-3 bg-[#0B5D3B] text-white hover:bg-[#08472D]" disabled={busy} onClick={handlePaste} data-testid={`${kind}-paste-save`}>
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Save text
          </Button>
        </div>
      )}
    </div>
  );
}

export function StepUpload({ assignment, reload }) {
  return (
    <div className="space-y-5" data-testid="step-upload">
      <div className="rounded-xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200">
        Only upload materials you're allowed to use for your own study. Remove identifying details of others, and check your institution's AI-use policy.
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <UploadBox assignmentId={assignment.id} kind="brief" title="Assignment brief" required
          hint="The question / task sheet from your course." current={!!assignment.brief_text} onUploaded={reload} />
        <UploadBox assignmentId={assignment.id} kind="rubric" title="Grading rubric"
          hint="The marking criteria. Skip for 'no rubric' mode." current={!!assignment.rubric_text} onUploaded={reload} />
        <UploadBox assignmentId={assignment.id} kind="draft" title="Your draft"
          hint="Your own writing, for evaluation. Add it any time." current={!!assignment.draft_text} onUploaded={reload} />
      </div>
      <MaterialsSection assignmentId={assignment.id} />
    </div>
  );
}
